import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useListener } from "@src/hooks/useListener";
import { useRadio } from "@src/hooks/useRadio";

import { usePlayerState } from "@src/states/player";

import { player } from "@src/services/player";
import { DEFAULT_TRACK_ID } from "@src/services/player/constants";
import { factory } from "@src/services/radioco";

import "./style.less";

function formatTime(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function CurrentTrackProgress() {
  const currentTrackId = usePlayerState((state) => state.currentTrackId);
  const onlineDuration = usePlayerState((state) => state.currentTrack.duration);
  const isOnlinePlaying = currentTrackId === DEFAULT_TRACK_ID;

  const { radio } = useRadio();
  const streaming_id = radio?.streaming_id || "";

  // Rádio online: tempo baseado em wall-clock (timestamp do início da música),
  // imune a overlays e à pausa — o stream continua no servidor. Overlays
  // (TTS/agendado/on-demand): contador próprio que corre enquanto tocam.
  const [onlineMs, setOnlineMs] = useState(0);
  const [overlayMs, setOverlayMs] = useState(0);
  const onlineStartRef = useRef<number | null>(null);
  const queryClient = useQueryClient();

  // Antes do primeiro changeTrack, a música já está rodando há um tempo
  // desconhecido no servidor — exibir 00:00 seria incorreto.
  const [onlineTrackStarted, setOnlineTrackStarted] = useState(false);

  const currentMs = isOnlinePlaying ? onlineMs : overlayMs;
  const showCurrentMs = isOnlinePlaying ? onlineTrackStarted : true;

  // Timestamp da última tentativa de refetch por overflow (0 = nunca).
  // Usar timestamp em vez de boolean permite retentar a cada 5s caso o
  // primeiro refetch não traga dados novos (Mercure com lag, API lenta, etc).
  const overflowRefetchedAtRef = useRef(0);

  // Zera o contador de overlay ao trocar de track (ajuste de estado em render,
  // padrão do React). Evita herdar o tempo do overlay anterior. O timer do rádio
  // online NÃO é tocado aqui — senão resetaria ao voltar de um overlay.
  const [prevTrackId, setPrevTrackId] = useState(currentTrackId);
  if (currentTrackId !== prevTrackId) {
    setPrevTrackId(currentTrackId);
    if (!isOnlinePlaying) setOverlayMs(0);
  }

  // changeTrack: a música online mudou → marca o novo início e zera o timer online.
  useListener(
    "changeTrack",
    useCallback(() => {
      setOnlineTrackStarted(true);
      onlineStartRef.current = Date.now();
      setOnlineMs(0);
      overflowRefetchedAtRef.current = 0;
    }, []),
  );

  // Tick de 1s. Online: recalcula do timestamp (segue mesmo pausado/durante overlay).
  // Outros: incrementa apenas enquanto o player estiver tocando.
  useEffect(() => {
    const interval = setInterval(() => {
      if (isOnlinePlaying) {
        if (!onlineTrackStarted || onlineStartRef.current === null) return;
        setOnlineMs(Date.now() - onlineStartRef.current);
      } else {
        if (player.getPlayingOrPaused(currentTrackId) !== "playing") return;
        setOverlayMs((prev) => prev + 1_000);
      }
    }, 1_000);
    return () => clearInterval(interval);
  }, [currentTrackId, isOnlinePlaying, onlineTrackStarted]);

  // Online: duração vem do Mercure (track_playout_duration), lida via selector reativo
  // Outros: duração lida do audioElement a cada re-render (disparado pelo tick de 1s)
  const durationMs = (() => {
    if (isOnlinePlaying) {
      return onlineDuration ?? 0;
    }
    try {
      const { audioElement } = player.getTrack(currentTrackId);
      const d = audioElement.duration;
      if (!d || Number.isNaN(d) || d === Infinity) return 0;
      return d * 1_000;
    } catch {
      return 0;
    }
  })();

  // Se o tempo decorrido ultrapassar a duração, força refetch dos metadados.
  // Retenta a cada 5s caso o refetch anterior não traga dados novos (Mercure com
  // lag, API lenta). No effect por ser side-effect — não pode rodar em render.
  useEffect(() => {
    if (isOnlinePlaying && durationMs > 0 && currentMs > durationMs) {
      const now = Date.now();
      if (now - overflowRefetchedAtRef.current > 5_000) {
        overflowRefetchedAtRef.current = now;
        console.log(
          "[CurrentTrackProgress] currentMs > durationMs, forçando refetch dos metadados",
        );
        // Contorna o cache do Mercure (#currentTrack) — sem isso o refetch abaixo
        // só releria o dado velho e nunca cairia no fallback HTTP.
        if (streaming_id) factory(streaming_id).invalidateCache();
        queryClient.invalidateQueries({ queryKey: ["tracks"] });
      }
    }
  }, [isOnlinePlaying, durationMs, currentMs, queryClient, streaming_id]);

  const progress =
    durationMs > 0 ? Math.min((currentMs / durationMs) * 100, 100) : 0;

  return (
    <div className="CurrentTrackProgress-component">
      <div className="labels">
        {showCurrentMs && (
          <span className="current">{formatTime(currentMs)}</span>
        )}
        {durationMs > 0 && (
          <span className="duration">{formatTime(durationMs)}</span>
        )}
      </div>
      <div className="bar-track">
        <div className="bar-fill" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}
